import type { IPathResult } from '../../astar/astarInterface'
import { messageWriter } from '../../engine/messageWriter-sidecar'
import { assert } from '../../lib/assert-sidecar'
import type { IIJ } from '../../lib/distanceUtil'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { common } from '../../main/common-sidecar'
import { EVoyageCrewCarryCommand } from './multi/voyageMessageCrewCarry'
import { voyageMessageCrewCarry } from './multi/voyageMessageCrewCarry-sidecar'
import type { IMessageCrewMove } from './multi/voyageMessageCrewMove'
import { voyageMessageCrewMove } from './multi/voyageMessageCrewMove-sidecar'
import { voyageAstar } from './voyageAstar-sidecar'
import { EVoyageCarryableType } from './voyageCarryableData'
import { EVoyageCrewType, IVoyageCrew } from './voyageCrew'
import { voyageCrew } from './voyageCrew-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import { voyageSystems_rack } from './voyageSystems_rack-sidecar'
import { EVoyageTile } from './voyageTileData'
import { voyageTilemap } from './voyageTilemap-sidecar'
import type { IVoyageTilemapPart } from './voyageTilemapPart'
import { voyageWebsockets } from './voyageWebsockets-sidecar'

export const footstepTime = 0.25
export const moveTime = 0.25
export const coffeeMultiplier = 0.5
const elevatorMoveTime = 0.5

export function lerpToIJ(obj: IVoyageCrew, iij: IIJ, moveTime: number) {
  obj.i = iij.i
  obj.j = iij.j
  let t = voyageTilemap.getSafe(iij.i, iij.j)
  voyageCrew.setTile(obj, t)
  if (t) {
    obj.sx = obj.x
    obj.sy = obj.y
    obj.tx = t.x
    obj.ty = t.y
    obj.isLerping = true
    obj.lerpTime = 0
    obj.lerpTimeMax = moveTime

    // console.log('lerp start')

    if (obj.type === EVoyageCrewType.Crew) {
      if (t.system) {
        if (t.system.data.onEnter) {
          t.system.data.onEnter(obj)
        } else {
          if (t.items.length > 0) {
            if (voyageWebsockets.getIsHost()) {
              voyageSystems_rack.swapItemHere(obj, false)
            }
          }
        }
      } else {
        if (t.items.length > 0) {
          if (voyageWebsockets.getIsHost()) {
            voyageSystems_rack.swapItemHere(obj, false)
          }
        }
      }
    }
  }
}

export function update(obj: IVoyageCrew) {
  let ds = common.getUpdateDeltaScaled()

  if (obj.isLerping) {
    // console.log('lerp update')
    obj.lerpTime += ds
    let t = obj.lerpTime / (obj.lerpTimeMax || 1)
    t = mathUtils.clamp(t, 0, 1)
    voyageCrew.moveToXY(
      obj,
      mathUtils.lerp(obj.sx, obj.tx, t),
      mathUtils.lerp(obj.sy, obj.ty, t),
    )
  }

  if (obj.path) {
    let ps = obj.path.path
    if (ps.length > 0) {
      obj.moveTimer += ds

      obj.footstepTimer += ds
      if (obj.footstepTimer > footstepTime) {
        obj.footstepTimer -= footstepTime
        // voyageSounds.playFootstep()
      }

      let moveMultiplier = 1
      if (voyageCrew.isHolding(obj, EVoyageCarryableType.Coffee)) {
        moveMultiplier *= coffeeMultiplier
      }
      let curMoveTime = moveTime * moveMultiplier

      if (obj.moveTimer > curMoveTime) {
        let nextStep = ps[obj.pathIdx]
        if (nextStep) {
          let tCur = voyageTilemap.getSafe(obj.i, obj.j)
          assert.notNil(tCur, 'tnext is null?')
          if (tCur.tile3.type === EVoyageTile.Elevator) {
            curMoveTime = elevatorMoveTime
          }
          if (obj.moveTimer > curMoveTime) {
            obj.moveTimer -= curMoveTime
            obj.pathIdx++

            lerpToIJ(obj, nextStep, curMoveTime)

            voyageSounds.playFootstep()

            if (obj.crewState && obj.crewState.coffeeSteps > 0) {
              obj.crewState.coffeeSteps--
              if (obj.crewState.coffeeSteps <= 0) {
                if (voyageWebsockets.getIsHost()) {
                  let coffee = voyageCrew.getItemSafe(
                    obj,
                    EVoyageCarryableType.Coffee,
                  )
                  if (coffee) {
                    voyageMessageCrewCarry.sendMessageAndImmediatelyExecute({
                      command: EVoyageCrewCarryCommand.Destroy,
                      type: EVoyageCarryableType.Coffee,
                      crewId: obj.id,
                      carryId1: coffee.id,
                      carryId2: 0,
                      i: obj.i,
                      j: obj.j,
                    })
                  }
                }
              }
            }
          }
        } else {
          obj.path = null
        }
      }
    }
  }
}

export function pathTo(obj: IVoyageCrew, i: number, j: number) {
  // if(voyageWebsockets.getIsHost()) {
  //   console.lo
  // }
  if (obj.isPassedOut) {
    return false
  }

  let tStart = voyageTilemap.getSafe(obj.i, obj.j)
  let tEnd = voyageTilemap.getSafe(i, j)
  obj.path = null

  let cantMoveHere = false
  if (tEnd && tStart) {
    if (tEnd.tile2.canWalk) {
      obj.path = voyageAstar.findPath(tStart, tEnd)

      if (obj.path.status !== 'success') {
        cantMoveHere = true
      }

      if (obj.path.path.length > 0) {
        let first = obj.path.path[0]
        if (first.i === obj.i && first.j === obj.j) {
          obj.path.path.shift() // First one is always current position
        }
      }

      if (obj.path.path.length <= 0) {
        obj.path = null
      } else {
        obj.pathIdx = 0
        obj.moveTimer = 0 // moveTime / 2
      }
    } else {
      cantMoveHere = true
    }
  }

  if (cantMoveHere) {
    voyageSounds.playCantMoveThere()
  }

  networkSendPathTo(obj)

  let foundPath = obj.path != null && obj.path.path.length > 0
  return foundPath
}

export function networkSendPathTo(obj: IVoyageCrew) {
  let path: [number, number][] = []
  if (obj.path) {
    path = l.map(obj.path.path, (c) => {
      return [c.i, c.j]
    })
  }
  voyageMessageCrewMove.SendMessageRequestAndImmediatelyExecute({
    crewId: obj.id,
    path,
  })
}

export function networkRecievePathTo(obj: IVoyageCrew, msg: IMessageCrewMove) {
  let path = l.map(msg.path, (c) => {
    return voyageTilemap.getUnsafe(c[0], c[1])
  })

  let pr: IPathResult<IVoyageTilemapPart> = {
    status: 'success',
    path,
  }
  obj.path = pr
  obj.pathIdx = 0
  obj.moveTimer = moveTime / 2
}
